/* eslint-disable */
// Chakra Imports
import { Box, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Flex, Icon, Link, Text, useColorModeValue } from '@chakra-ui/react';
import { MdChevronRight } from 'react-icons/md';
import NavbarLinks from 'components/navbar/NavbarLinks';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import routes from 'routes';
import { accessibleAdminRoutes } from 'routes/access';
import { Layout, RType } from 'routes/types';
import { useUserRole } from '../../features/auth/roles';

export default function Navbar(props: {
	secondary: boolean;
	message: string|boolean;
	brandText: string;
	logoText: string;
	fixed: boolean;
	layout: Layout;
	onOpen: (...args: any[]) => any;
	enableFavorites?: boolean;
	onCommandPaletteOpen?: () => void;
	// The role/selection-filtered sidebar routes from the layout. The
	// responsive (hamburger) sidebar renders from THIS list — falling back to
	// the static route table here is how coaches once got the full admin menu
	// on narrow viewports.
	visibleRoutes?: RType[];
}) {
	const [ scrolled, setScrolled ] = useState(false);
	const location = useLocation();
	const role = useUserRole();

	useEffect(() => {
		window.addEventListener('scroll', changeNavbar);

		return () => {
			window.removeEventListener('scroll', changeNavbar);
		};
	});

	const { secondary,  brandText } = props;

	// Build breadcrumbs from current route
	const breadcrumbs = useMemo(() => {
		// Restricted roles (coaches) only ever see crumbs they can actually
		// click — a link to a page their layout would bounce them off is worse
		// than no link at all.
		const allRoutes = props.layout === '/admin'
			? accessibleAdminRoutes(role)
			: routes[props.layout] || [];
		const pathname = location.pathname;
		// Find the most specific matching route (longest path wins)
		const matched = allRoutes
			.filter(r => r.layout === '/admin' && pathname.startsWith(r.layout + r.path.replace(/:[^/]+/g, '')))
			.sort((a, b) => b.path.length - a.path.length)[0]
			?? allRoutes.find(r => r.layout === '/admin' && pathname === r.layout + r.path);
		if (!matched || matched.path === '/home') return [];

		const homeRoute = allRoutes.find(r => r.layout === '/admin' && r.path === '/home');
		const crumbs = homeRoute
			? [{ label: homeRoute.name, path: '/admin/home' }]
			: [];

		// For hidden detail pages, find a visible parent
		if (matched.hidden) {
			// Explicit detail→list candidates for routes without a group. The
			// first accessible candidate wins: admins get the canonical list
			// page, while a coach on /gameDetails (whose /latest-booked-games
			// is admin-only) falls back to the calendar they came from.
			const detailToParents: Record<string, string[]> = {
				'/userDetails': ['/users'],
				'/courtDetails': ['/courts'],
				'/gameDetails': ['/latest-booked-games', '/calendar'],
				'/eventDetails': ['/events', '/calendar'],
				'/coachDetails': ['/coachs'],
				'/coachingSessionDetails': ['/coachs'],
				'/locationDetails': ['/locations'],
				'/video-review-detail': ['/video-reviews'],
			};

			let parentRoute: typeof allRoutes[number] | undefined;

			// 1) Same group if available
			if (matched.group) {
				parentRoute = allRoutes.find(r =>
					!r.hidden && r.group === matched.group && r.layout === '/admin'
				);
			}

			// 2) Explicit path mapping
			if (!parentRoute) {
				for (const candidate of detailToParents[matched.path] ?? []) {
					parentRoute = allRoutes.find(r =>
						r.layout === '/admin' && r.path === candidate
					);
					if (parentRoute) break;
				}
			}

			if (parentRoute) {
				crumbs.push({ label: parentRoute.name, path: parentRoute.layout + parentRoute.path });
			}
		}

		crumbs.push({ label: matched.name, path: matched.layout + matched.path });
		return crumbs;
	}, [props.layout, role, location.pathname]);

	// Here are all the props that may change depending on navbar's type or state.(secondary, variant, scrolled)
	let mainText = useColorModeValue('navy.700', 'white');
	let secondaryText = useColorModeValue('gray.700', 'white');
	let navbarPosition = 'fixed' as const;
	let navbarFilter = 'none';
	let navbarBackdrop = 'blur(20px)';
	let navbarBg = useColorModeValue(
		'var(--navbar-bg-light, rgba(247, 248, 249, 0.2))',
		'var(--navbar-bg-dark, rgba(9, 9, 11, 0.5))'
	);
	let navbarBorder = 'transparent';
	let secondaryMargin = '0px';
	let paddingX = '15px';
	let gap = '0px';

	// Scroll-triggered shadow
	const scrolledShadow = useColorModeValue(
		'var(--chakra-shadows-sm, 0px 4px 12px rgba(112, 144, 176, 0.12))',
		'0px 4px 12px rgba(0, 0, 0, 0.3)'
	);
	const scrolledBorder = useColorModeValue(
		'secondaryGray.100',
		'rgba(255, 255, 255, 0.05)'
	);
	const navbarShadow = scrolled ? scrolledShadow : 'none';
	const navbarBorderColor = scrolled ? scrolledBorder : 'transparent';

	const changeNavbar = () => {
		if (window.scrollY > 1) {
			setScrolled(true);
		} else {
			setScrolled(false);
		}
	};

	return (
		<Box
			position={navbarPosition}
			boxShadow={navbarShadow}
			bg={navbarBg}
			borderColor={navbarBorderColor}
			filter={navbarFilter}
			backdropFilter={navbarBackdrop}
			backgroundPosition='center'
			backgroundSize='cover'
			borderRadius='14px'
			borderWidth='1.5px'
			borderStyle='solid'
			transitionDelay='0s, 0s, 0s, 0s'
			transitionDuration=' 0.25s, 0.25s, 0.25s, 0s'
			transition-property='box-shadow, background-color, filter, border'
			transitionTimingFunction='linear, linear, linear, linear'
			alignItems={{ xl: 'center' }}
			display={secondary ? 'block' : 'flex'}
			minH='75px'
			justifyContent={{ xl: 'center' }}
			lineHeight='25.6px'
			mx='auto'
			mt={secondaryMargin}
			pb='8px'
			right={{ base: '12px', md: '30px', lg: '30px', xl: '30px' }}
			px={{
				sm: paddingX,
				md: '10px'
			}}
			ps={{
				xl: '12px'
			}}
			pt='8px'
			zIndex={10}
			top={{ base: '12px', md: '16px', xl: '18px' }}
			w={{
				base: 'calc(100vw - 6%)',
				md: 'calc(100vw - 8%)',
				lg: 'calc(100vw - 6%)',
				xl: 'calc(100vw - 350px)',
				'2xl': 'calc(100vw - 365px)'
			}}>
			<Flex
				w='100%'
				flexDirection={{
					sm: 'column',
					md: 'row'
				}}
				alignItems={{ xl: 'center' }}
				mb={gap}>
				<Box mb={{ sm: '8px', md: '0px' }}>
					{breadcrumbs.length > 0 && (
						<Breadcrumb
							separator={<Icon as={MdChevronRight} color={secondaryText} w='14px' h='14px' verticalAlign='middle' />}
							spacing='4px'
						>
							{breadcrumbs.map((crumb, i) => {
								const isLast = i === breadcrumbs.length - 1;
								return (
									<BreadcrumbItem key={crumb.path} isCurrentPage={isLast}>
										<BreadcrumbLink
											as={isLast ? undefined : RouterLink}
											to={isLast ? undefined : crumb.path}
											color={isLast ? mainText : secondaryText}
											fontSize='xs'
											fontFamily='"Urbanist", sans-serif'
											fontWeight={isLast ? '600' : '500'}
											letterSpacing='0.02em'
											_hover={isLast ? {} : { color: mainText }}
										>
											{crumb.label}
										</BreadcrumbLink>
									</BreadcrumbItem>
								);
							})}
						</Breadcrumb>
					)}
				</Box>
				<Box ms='auto' w={{ sm: '100%', md: 'unset' }}>
					<NavbarLinks
						onOpen={props.onOpen}
						secondary={props.secondary}
						fixed={props.fixed}
						routes={props.visibleRoutes ?? routes[props.layout]}
						enableFavorites={props.enableFavorites}
						onCommandPaletteOpen={props.onCommandPaletteOpen}
					/>
				</Box>
			</Flex>
		</Box>
	);
}
